/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetGcpIamCredentialRequest,
  GetGcpIamCredentialSecurity,
} from "../models/operations/getgcpiamcredential.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetGcpIamCredentialQuery,
  GetGcpIamCredentialQueryData,
  prefetchGetGcpIamCredential,
  queryKeyGetGcpIamCredential,
} from "./getGcpIamCredential.core.js";
export {
  buildGetGcpIamCredentialQuery,
  type GetGcpIamCredentialQueryData,
  prefetchGetGcpIamCredential,
  queryKeyGetGcpIamCredential,
};

export type GetGcpIamCredentialQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getGcpIamCredential externalCredentials
 *
 * @remarks
 * Get a GCP IAM external credential by ID. Requires org:read.
 */
export function useGetGcpIamCredential(
  request: GetGcpIamCredentialRequest,
  security?: GetGcpIamCredentialSecurity | undefined,
  options?: QueryHookOptions<
    GetGcpIamCredentialQueryData,
    GetGcpIamCredentialQueryError
  >,
): UseQueryResult<GetGcpIamCredentialQueryData, GetGcpIamCredentialQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGetGcpIamCredentialQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getGcpIamCredential externalCredentials
 *
 * @remarks
 * Get a GCP IAM external credential by ID. Requires org:read.
 */
export function useGetGcpIamCredentialSuspense(
  request: GetGcpIamCredentialRequest,
  security?: GetGcpIamCredentialSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetGcpIamCredentialQueryData,
    GetGcpIamCredentialQueryError
  >,
): UseSuspenseQueryResult<
  GetGcpIamCredentialQueryData,
  GetGcpIamCredentialQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetGcpIamCredentialQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetGcpIamCredentialData(
  client: QueryClient,
  queryKeyBase: [parameters: { id: string; gramSession?: string | undefined }],
  data: GetGcpIamCredentialQueryData,
): GetGcpIamCredentialQueryData | undefined {
  const key = queryKeyGetGcpIamCredential(...queryKeyBase);

  return client.setQueryData<GetGcpIamCredentialQueryData>(key, data);
}

export function invalidateGetGcpIamCredential(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { id: string; gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "externalCredentials",
      "getGcpIam",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllGetGcpIamCredential(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "externalCredentials", "getGcpIam"],
  });
}
